import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ChevronLeft, Loader2, GripVertical } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBranding } from "@/context/BrandingContext";
import ProgramSelection from "./steps/ProgramSelection";
import PersonalInfo from "./steps/PersonalInfo";
import EducationalHistory from "./steps/EducationalHistory";
import Questionnaire, { type QuestionnaireHandle } from "./steps/Questionnaire";
import Documents from "./steps/Documents";
import CompleteScreen from "./steps/CompleteScreen";
import ApplicationTypeSelection from "./steps/ApplicationTypeSelection";
import type { Application } from "../../types";
import { getIntakeTerms, getDegrees, getProgramsByTerm, createApplication, submitApplication, savePersonalInfo, getCountries, getApplicantProfile, IntakeTerm, Degree, HubProgram } from "@/services/universityService";

const baseSteps = [
    { id: "selection", label: "Program Selection" },
    { id: "personal", label: "Personal Information" },
    { id: "educational", label: "Educational History" },
    { id: "questionnaire", label: "Questionnaire" },
    { id: "documents", label: "Documents" },
    { id: "completed", label: "Completed" },
];


export default function ApplicationForm({ onCancel, onSubmit, onApplicationCreated, onStepChange, initialStep = 0, applicationNumber, initialTerm, initialDegree, initialProgram, initialPrograms, initialApplicationType }: { onCancel: (partial?: Application) => void; onSubmit: (app: Application) => void; onApplicationCreated?: (appNumber: string) => void; onStepChange?: (appNumber: string, step: number) => void; initialStep?: number; applicationNumber?: string; initialTerm?: string; initialDegree?: string; initialProgram?: number; initialPrograms?: number[]; initialApplicationType?: string }) {
    const { branding } = useBranding();
    const programsPerPage = branding?.branding?.programs_per_page ?? 10;
    const personalInfoFieldConfig = branding?.branding?.personal_info_form_config;

    // Determine if application type step should be shown
    const showTransfer = branding?.branding?.show_transfer_option ?? false;
    const showExchange = branding?.branding?.show_exchange_option ?? false;
    const showApplicationTypeStep = showTransfer || showExchange;

    // Build steps array dynamically
    const steps = useMemo(() => {
        if (showApplicationTypeStep) {
            return [
                { id: "application-type", label: "Application Type" },
                ...baseSteps,
            ];
        }
        return baseSteps;
    }, [showApplicationTypeStep]);

    const [appNumber, setAppNumber] = useState(() => applicationNumber ?? "");
    const [currentStep, setCurrentStep] = useState(initialStep);
    
    // Skip Application Type step if we have preselected values (after branding loads)
    const [hasAutoSkippedType, setHasAutoSkippedType] = useState(false);
    useEffect(() => {
        // Only run once when branding loads and we have preselected values
        if (
            !hasAutoSkippedType &&
            branding !== null &&
            showApplicationTypeStep &&
            initialApplicationType &&
            initialTerm &&
            initialDegree &&
            initialProgram &&
            currentStep === 0
        ) {
            setCurrentStep(1); // Skip to Program Selection step
            setHasAutoSkippedType(true);
        }
    }, [branding, showApplicationTypeStep, initialApplicationType, initialTerm, initialDegree, initialProgram, currentStep, hasAutoSkippedType]);
    
    const currentStepId = steps[currentStep]?.id;
    const isFirstStep = currentStep === 0;
    const isCompletedStep = currentStepId === "completed";
    const [selectedApplicationType, setSelectedApplicationType] = useState<string | null>(
        initialApplicationType ?? null
    );

    // Reconcile selectedApplicationType when branding loads (async) and showApplicationTypeStep resolves
    useEffect(() => {
        // Only set default if no type selected and we know whether the step is shown
        if (selectedApplicationType === null && branding !== null) {
            if (!showApplicationTypeStep) {
                setSelectedApplicationType("First Year Student");
            }
        }
    }, [branding, showApplicationTypeStep, selectedApplicationType]);
    const [selectedTerm, setSelectedTerm] = useState<string | null>(initialTerm ?? null);
    const [selectedProgram, setSelectedProgram] = useState<number | null>(initialProgram ?? null);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>(
        initialPrograms?.length ? initialPrograms : initialProgram != null ? [initialProgram] : []
    );
    const [showReorderModal, setShowReorderModal] = useState(false);

    // Focus management and ESC key handling for the reorder modal
    useEffect(() => {
        if (!showReorderModal) return;

        const modalPanel = reorderModalRef.current;
        if (!modalPanel) return;

        // Save the element that had focus before the modal opened so we can restore it on close
        const previouslyFocused = document.activeElement as HTMLElement | null;

        // Move focus into the modal — first focusable element
        const focusableSelectors =
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const firstFocusable = modalPanel.querySelector<HTMLElement>(focusableSelectors);
        firstFocusable?.focus();

        const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
            if (keyboardEvent.key === "Escape") {
                setShowReorderModal(false);
                return;
            }

            if (keyboardEvent.key !== "Tab") return;

            const focusableElements = Array.from(
                modalPanel.querySelectorAll<HTMLElement>(focusableSelectors)
            ).filter((element) => !element.closest("[disabled]"));

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (keyboardEvent.shiftKey) {
                if (document.activeElement === firstElement) {
                    keyboardEvent.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    keyboardEvent.preventDefault();
                    firstElement.focus();
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            // Restore focus to the element that opened the modal
            previouslyFocused?.focus();
        };
    }, [showReorderModal]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDegree, setSelectedDegree] = useState<string | null>(initialDegree ?? null);
    const [intakeTerms, setIntakeTerms] = useState<IntakeTerm[]>([]);
    const [degrees, setDegrees] = useState<Degree[]>([]);
    const [hubPrograms, setHubPrograms] = useState<HubProgram[]>([]);
    const [loadingTerms, setLoadingTerms] = useState(true);
    const [loadingDegrees, setLoadingDegrees] = useState(false);
    const [loadingPrograms, setLoadingPrograms] = useState(false);

    // Filter terms based on selected application type
    const filteredTerms = useMemo(() => {
        const appType = selectedApplicationType || "First Year Student";
        return intakeTerms.filter((term) => {
            const types = term.available_application_types || [];
            // If no types defined, show for all application types
            if (types.length === 0) return true;
            return types.includes(appType);
        });
    }, [intakeTerms, selectedApplicationType]);

    // Filter degrees based on selected application type
    const filteredDegrees = useMemo(() => {
        const appType = selectedApplicationType || "First Year Student";
        return degrees.filter((degree) => {
            const types = degree.available_application_types || [];
            // If no types defined, show for all application types
            if (types.length === 0) return true;
            return types.includes(appType);
        });
    }, [degrees, selectedApplicationType]);

    // Reset selected term when application type changes if current term doesn't support new type
    // Also auto-select if only one filtered term is available
    useEffect(() => {
        if (loadingTerms) return;

        if (filteredTerms.length === 0) {
            // Clear all dependent selections when no terms match
            setSelectedTerm(null);
            setSelectedDegree(null);
            setSelectedProgram(null);
            setSelectedPrograms([]);
            return;
        }

        if (selectedTerm) {
            const matchedTerm = filteredTerms.find((t: IntakeTerm) => t.id === selectedTerm || t.name === selectedTerm);
            if (!matchedTerm) {
                // Current term not valid for this app type - reset and auto-select if only one
                if (filteredTerms.length === 1) {
                    setSelectedTerm(filteredTerms[0].id);
                } else {
                    setSelectedTerm(null);
                }
                setSelectedDegree(null);
                setSelectedProgram(null);
                setSelectedPrograms([]);
            } else if (matchedTerm.id !== selectedTerm) {
                setSelectedTerm(matchedTerm.id);
            }
        } else if (filteredTerms.length === 1) {
            // No term selected, auto-select if only one available
            setSelectedTerm(filteredTerms[0].id);
        }
    }, [selectedApplicationType, filteredTerms, selectedTerm, loadingTerms]);

    // Reset selected degree when filtered degrees change if current degree doesn't support new type
    useEffect(() => {
        if (filteredDegrees.length === 0) {
            // Clear dependent selections when no degrees match
            setSelectedDegree(null);
            setSelectedProgram(null);
            setSelectedPrograms([]);
            return;
        }
        
        if (selectedDegree) {
            const degreeStillValid = filteredDegrees.some((d) => d.degree === selectedDegree);
            if (!degreeStillValid) {
                setSelectedDegree(null);
                setSelectedProgram(null);
                setSelectedPrograms([]);
            }
        }
    }, [selectedApplicationType, filteredDegrees, selectedDegree]);

    const [countries, setCountries] = useState<string[]>([]);
    const [creatingApplication, setCreatingApplication] = useState(false);
    const [submittingApplication, setSubmittingApplication] = useState(false);
    const [eduRequirementMet, setEduRequirementMet] = useState(true);
    const [showEduWarning, setShowEduWarning] = useState(false);
    const [docsRequirementMet, setDocsRequirementMet] = useState(true);
    const [showDocsWarning, setShowDocsWarning] = useState(false);
    const [questionnaireRequirementMet, setQuestionnaireRequirementMet] = useState(true);
    const [showQuestionnaireWarning, setShowQuestionnaireWarning] = useState(false);
    const questionnaireRef = useRef<QuestionnaireHandle>(null);
    const reorderModalRef = useRef<HTMLDivElement>(null);
    const [personalInfo, setPersonalInfo] = useState({
        firstName: "", preferredName: "", lastName: "", citizenship: "", secondaryCitizenship: "",
        gender: "", nationalIdentityNumber: "", dateOfBirth: "", countryOfBirth: "", cityOfBirth: "",
        identityNumber: "", identityDateOfIssue: "", identityDateOfExpiry: "", identityIssuingCountry: "",
        email: "", mobileCode: "", mobileNumber: "",
        fatherName: "", motherName: "",
        addressCountry: "", addressStreet: "", addressCity: "", addressZip: "",
        isAlumni: false,
    });
    const [salesforceIds, setSalesforceIds] = useState<{ salesforce_id?: string; account_salesforce_id?: string; application_salesforce_id?: string }>({});
    const [programSelectionError, setProgramSelectionError] = useState<string | null>(null);
    const [personalInfoError, setPersonalInfoError] = useState<string | null>(null);
    const [savingPersonalInfo, setSavingPersonalInfo] = useState(false);
    const [savingQuestionnaire, setSavingQuestionnaire] = useState(false);

    const handlePersonalInfoChange = (field: string, value: string | boolean) =>
        setPersonalInfo((prev) => ({ ...prev, [field]: value }));

    // Load terms and countries once on mount
    useEffect(() => {
        setLoadingTerms(true);
        Promise.all([getIntakeTerms(), getCountries()])
            .then(([termsRes, countriesRes]) => {
                setIntakeTerms(termsRes.terms);
                if (countriesRes.status === 'success') {
                    setCountries(countriesRes.countries);
                }
            })
            .catch(() => {})
            .finally(() => setLoadingTerms(false));
    }, []);

    useEffect(() => {
        setDegrees([]);
        setSelectedDegree(null);
        if (!selectedTerm) return;
        setLoadingDegrees(true);
        getDegrees(selectedTerm)
            .then((res) => {
                if (res.status === 'success') {
                    setDegrees(res.degrees);
                    if (initialDegree) {
                        const exists = res.degrees.some((deg) => deg.degree === initialDegree);
                        setSelectedDegree(exists ? initialDegree : null);
                    }
                }
            })
            .catch(() => {})
            .finally(() => setLoadingDegrees(false));
    }, [selectedTerm, intakeTerms]);

    // Load applicant profile once on mount and pre-fill personal info
    useEffect(() => {
        getApplicantProfile()
            .then(async (res) => {
                if (res.status === 'success' && res.profile) {
                    const profile = res.profile;

                    // Detect country phone code: saved value takes priority,
                    // then IP geolocation (ipinfo.io) with navigator.language as fallback.
                    let detectedCode = profile.country_phone_code || "";
                    if (!detectedCode) {
                        const localeIso = (navigator.language || "").split("-")[1]?.toUpperCase() ?? "";
                        let isoFromIp = "";
                        try {
                            const geoRes = await fetch("https://ipinfo.io/json");
                            if (geoRes.ok) {
                                const geoData = await geoRes.json();
                                isoFromIp = geoData.country?.toUpperCase() ?? "";
                            }
                        } catch {
                            // network failed — fall through to locale
                        }
                        const resolvedIso = isoFromIp || localeIso;
                        if (resolvedIso) {
                            const { COUNTRY_CODES } = await import("@/utils/countryCodes");
                            const match = COUNTRY_CODES.find((c) => c.iso === resolvedIso);
                            if (match) detectedCode = match.code;
                        }
                    }

                    setPersonalInfo((prev: typeof personalInfo) => ({
                        ...prev,
                        ...(profile.first_name && { firstName: profile.first_name }),
                        ...(profile.last_name && { lastName: profile.last_name }),
                        ...(profile.preferred_name && { preferredName: profile.preferred_name }),
                        ...(profile.email && { email: profile.email }),
                        ...(profile.citizenship && { citizenship: profile.citizenship }),
                        ...(profile.secondary_citizenship && { secondaryCitizenship: profile.secondary_citizenship }),
                        ...(profile.gender && { gender: profile.gender }),
                        ...(profile.national_identity_number && { nationalIdentityNumber: profile.national_identity_number }),
                        ...(profile.date_of_birth && { dateOfBirth: profile.date_of_birth }),
                        ...(profile.country_of_birth && { countryOfBirth: profile.country_of_birth }),
                        ...(profile.city_of_birth && { cityOfBirth: profile.city_of_birth }),
                        ...(profile.phone && { mobileNumber: profile.phone }),
                        ...(detectedCode && { mobileCode: detectedCode }),
                        ...(profile.father_name && { fatherName: profile.father_name }),
                        ...(profile.mother_name && { motherName: profile.mother_name }),
                        ...(profile.address_country && { addressCountry: profile.address_country }),
                        ...(profile.address_street && { addressStreet: profile.address_street }),
                        ...(profile.address_city && { addressCity: profile.address_city }),
                        ...(profile.address_zip && { addressZip: profile.address_zip }),
                        // Identity document fields
                        ...(profile.identity_number && { identityNumber: profile.identity_number }),
                        ...(profile.identity_date_of_issue && { identityDateOfIssue: profile.identity_date_of_issue }),
                        ...(profile.identity_date_of_expiry && { identityDateOfExpiry: profile.identity_date_of_expiry }),
                        ...(profile.identity_issuing_country && { identityIssuingCountry: profile.identity_issuing_country }),
                    }));
                }
            })
            .catch(() => {});
    }, []);

    // Fetch programs from DB whenever the user picks a term + degree + application type combination
    useEffect(() => {
        if (!selectedTerm || !selectedDegree) {
            setSelectedProgram(null);
            setSelectedPrograms([]);
            setHubPrograms([]);
            return;
        }
        const appType = selectedApplicationType || "First Year Student";
        setLoadingPrograms(true);
        getProgramsByTerm(selectedTerm, selectedDegree, appType)
            .then((res) => {
                if (res.status === 'success') {
                    setHubPrograms(res.programs);
                    // Restore pre-selected programs only if they exist in the loaded list
                    const idsToRestore = initialPrograms?.length
                        ? initialPrograms
                        : initialProgram != null ? [initialProgram] : [];

                    if (idsToRestore.length > 0) {
                        // Match each saved PLE id to a program in the loaded list (by id or price_list_entry_id)
                        const matchedIds: number[] = [];
                        for (const savedId of idsToRestore) {
                            const match = res.programs.find((prog) =>
                                prog.id === savedId || String(prog.price_list_entry_id) === String(savedId)
                            );
                            if (match) matchedIds.push(match.id);
                        }
                        if (matchedIds.length > 0) {
                            setSelectedPrograms(matchedIds);
                            setSelectedProgram(matchedIds[0]);
                        } else {
                            setSelectedPrograms([]);
                            setSelectedProgram(null);
                        }
                    } else {
                        setSelectedPrograms([]);
                        setSelectedProgram(null);
                    }
                }
            })
            .catch(() => {})
            .finally(() => setLoadingPrograms(false));
    }, [selectedTerm, selectedDegree, selectedApplicationType]);

    // Push a history entry so browser back button works
    useEffect(() => {
        window.history.pushState({ applicationForm: true }, "");
        const handlePopState = () => {
            onCancel();
        };
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    // Keep single-value selectedProgram in sync for backward-compat uses (selectedProgramData etc.)
    // It always reflects the first (primary) choice.
    useEffect(() => {
        setSelectedProgram(selectedPrograms.length > 0 ? selectedPrograms[0] : null);
    }, [selectedPrograms]);

    // Derive max selectable PLEs from the currently selected degree record
    const maxSelectable = useMemo(() => {
        const degreeRecord = filteredDegrees.find((deg) => deg.degree === selectedDegree);
        return degreeRecord?.max_ple_for_same_institution ?? 3;
    }, [filteredDegrees, selectedDegree]);

    const handleCreateApplication = async () => {
        const degreeRecord = filteredDegrees.find((deg) => deg.degree === selectedDegree);
        setProgramSelectionError(null);
        setCreatingApplication(true);
        try {
            const res = await createApplication({
                ...(appNumber ? { application_number: appNumber } : {}),
                selected_entries: selectedPrograms.map((id) => {
                    const prog = hubPrograms.find((p) => p.id === id);
                    return prog?.price_list_entry_id ?? String(id);
                }),
                term_id: selectedTerm!,
                degree_setting_id: degreeRecord?.id ?? selectedDegree!,
                application_type: selectedApplicationType || "First Year Student",
            });

            // Handle max_application_reached error
            if (res?.status === "error" && res?.code === "max_application_reached") {
                const maxApplication = res?.max_application ?? 3;
                setProgramSelectionError(
                    `You have reached the maximum number of applications ${maxApplication} for this degree and term.`
                );
                return; // stay on program selection step
            }

            // Handle institution_not_accepting_applications: the university takes
            // applications through its own admissions office, not this portal.
            // Only reachable for student sessions — agency users are exempt server-side.
            if (res?.status === "error" && res?.code === "institution_not_accepting_applications") {
                const blocked = res?.institutions ?? [];
                setProgramSelectionError(
                    blocked.length > 0
                        ? `${blocked.join(", ")} ${blocked.length > 1 ? "are" : "is"} not accepting online applications. Please remove ${blocked.length > 1 ? "those programs" : "that program"} and contact the university directly.`
                        : "One of the selected programs is not accepting online applications. Please remove it and contact the university directly."
                );
                return; // stay on program selection step
            }

            if (res?.status !== "success") {
                setProgramSelectionError("Failed to create application. Please try again.");
                return;
            }
            const realAppNumber = res?.result?.applicationNumber;
            if (realAppNumber && !appNumber) {
                setAppNumber(realAppNumber);
                onApplicationCreated?.(realAppNumber);
                onStepChange?.(realAppNumber, getStepIndex("personal"));
            }
            const appSfId = res?.result?.applicationSalesforceId;
            if (appSfId) {
                setSalesforceIds((prev: typeof salesforceIds) => ({ ...prev, application_salesforce_id: appSfId }));
            }
            setShowReorderModal(false);
            nextStep();
        } catch {
            setProgramSelectionError("Failed to create application. Please try again.");
        } finally {
            setCreatingApplication(false);
        }
    };

    const selectedProgramData = hubPrograms.find((program) => program.id === (selectedPrograms[0] ?? selectedProgram));
    const selectedTermName = intakeTerms.find((term) => term.id === selectedTerm)?.name || "";
    const documentsSummary = {
        fullName: `${personalInfo.firstName} ${personalInfo.lastName}`.trim(),
        appliedTerm: selectedTermName,
        email: personalInfo.email,
        mobile: `${personalInfo.mobileCode} ${personalInfo.mobileNumber}`.trim(),
        degree: selectedProgramData?.degree_type || "",
        stage: "Evaluation",
        citizenship: personalInfo.citizenship,
        appliedProgram: selectedProgramData?.name || "",
    };

    const bypassCvForm = branding?.branding?.bypass_cv_form ?? false;

    // Helper to find step index by id
    const getStepIndex = (stepId: string) => steps.findIndex((s) => s.id === stepId);

    const nextStep = () => {
        if (currentStep < steps.length - 1) {
            let nextStepIndex = currentStep + 1;
            // Skip Educational History if bypass_cv_form is enabled
            const educationalIndex = getStepIndex("educational");
            if (bypassCvForm && nextStepIndex === educationalIndex) {
                nextStepIndex = educationalIndex + 1;
            }
            setCurrentStep(nextStepIndex);
            requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }));
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            let prevStepIndex = currentStep - 1;
            // Skip Educational History when going back if bypass_cv_form is enabled
            const educationalIndex = getStepIndex("educational");
            if (bypassCvForm && prevStepIndex === educationalIndex) {
                prevStepIndex = educationalIndex - 1;
            }
            setCurrentStep(prevStepIndex);
            requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }));
        }
    };

    const buildFinalApp = (): Application => ({
        Application_Number: appNumber,
        Application_External_ID: "",
        Applicant_Name: documentsSummary.fullName || "—",
        Applied_Program_Name: documentsSummary.appliedProgram || "—",
        Stage: "Evaluation",
        Applied_Term: documentsSummary.appliedTerm || "—",
        Application_Status: "",
        Action: "View",
        Updated_At: "Just now",
        Date: new Date(),
        summary: documentsSummary,
    });

    return (
        <>
        <div className="w-full">
            {/* Breadcrumb */}
            <button
                onClick={() => onCancel()}
                className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-[#001B44] hover:text-[#002b66] transition-colors mb-4"
            >
                <ChevronLeft className="w-3.5 h-3.5" />
                All Applications
            </button>

            {/* Mobile horizontal stepper */}
            <div className="md:hidden mb-4 px-1">
                <div className="flex items-center justify-between">
                    {steps.map((step, index) => (
                        <div key={step.id} className="flex items-center flex-1 last:flex-none">
                            <button
                                onClick={() =>
                                    !isCompletedStep &&
                                    index <= currentStep &&
                                    setCurrentStep(index)
                                }
                                className="flex flex-col items-center gap-1 shrink-0"
                            >
                                <div
                                    className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${
                                        index < currentStep || (index === currentStep && isCompletedStep)
                                            ? "bg-emerald-500 border-emerald-500 text-white"
                                            : index === currentStep
                                              ? "bg-white border-[#001B44] text-[#001B44] ring-2 ring-slate-100"
                                              : "bg-white border-slate-200 text-slate-300"
                                    }`}
                                >
                                    {index < currentStep || (index === currentStep && isCompletedStep) ? (
                                        <Check className="h-3.5 w-3.5" />
                                    ) : (
                                        <span className="text-[10px] font-black">{index + 1}</span>
                                    )}
                                </div>
                            </button>
                            {index !== steps.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-1 transition-colors duration-500 ${index < currentStep ? "bg-emerald-500" : "bg-slate-100"}`} />
                            )}
                        </div>
                    ))}
                </div>
                <p className="text-center text-[10px] font-black uppercase tracking-widest text-[#001B44] mt-2">
                    Step {currentStep + 1} — {steps[currentStep]?.label}
                </p>
            </div>

            {/* Form Content */}
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 md:gap-10">
                        {/* Sidebar Stepper — desktop only */}
                        <aside className="hidden md:block w-full md:w-80 shrink-0">
                            <div className="bg-white border border-slate-200 rounded-3xl p-8 sticky top-28 shadow-sm">
                                <div className="mb-10">
                                    <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">
                                        Application Steps
                                    </h3>
                                    <div className="h-1 w-10 bg-[#001B44] rounded-full" />
                                </div>
                                <nav className="flex flex-col gap-4">
                                    {steps.map((step, index) => (
                                        <div key={step.id} className="relative">
                                            {index !== steps.length - 1 && (
                                                <div
                                                    className={`absolute left-7 top-12 w-0.5 h-8 transition-colors duration-500 ${
                                                        index < currentStep
                                                            ? "bg-slate-400"
                                                            : "bg-slate-100"
                                                    }`}
                                                />
                                            )}
                                            <button
                                                onClick={() =>
                                                    !isCompletedStep &&
                                                    index <= currentStep &&
                                                    setCurrentStep(index)
                                                }
                                                className={`flex items-center gap-5 w-full p-2 rounded-2xl transition-all ${
                                                    index === currentStep
                                                        ? "text-[#001B44]"
                                                        : "text-slate-400"
                                                } ${isCompletedStep || index > currentStep ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-slate-50"}`}
                                            >
                                                <div
                                                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all shrink-0 ${
                                                        index < currentStep || (index === currentStep && isCompletedStep)
                                                            ? "bg-emerald-500 border-emerald-500 text-white shadow-sm"
                                                            : index === currentStep
                                                              ? "bg-white border-[#001B44] text-[#001B44] shadow-sm ring-4 ring-slate-50"
                                                              : "bg-white border-slate-100 text-slate-300"
                                                    }`}
                                                >
                                                    {index < currentStep || (index === currentStep && isCompletedStep) ? (
                                                        <Check className="h-5 w-5" />
                                                    ) : (
                                                        <span className="text-xs font-black">
                                                            {index + 1}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-left overflow-hidden">
                                                    <p className="text-[11px] font-black uppercase tracking-widest truncate">
                                                        {step.label}
                                                    </p>
                                                    {index === currentStep && (
                                                        <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5 tracking-tighter">
                                                            Current Section
                                                        </p>
                                                    )}
                                                </div>
                                            </button>
                                        </div>
                                    ))}
                                </nav>
                            </div>
                        </aside>

                        {/* Main Content Area */}
                        <div className="flex-1 min-w-0">
                            <Card className="border border-slate-200 bg-white rounded-2xl md:rounded-[2rem] shadow-sm flex flex-col overflow-hidden">
                                <CardContent className="p-5 sm:p-8 md:p-12 flex-1 flex flex-col">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={currentStep}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.3 }}
                                            className="flex-1 flex flex-col"
                                        >
                                            {steps[currentStep]?.id === "application-type" && (
                                                <ApplicationTypeSelection
                                                    selectedType={selectedApplicationType}
                                                    onSelect={setSelectedApplicationType}
                                                    showTransfer={showTransfer}
                                                    showExchange={showExchange}
                                                />
                                            )}

                                            {steps[currentStep]?.id === "selection" && (
                                                <ProgramSelection
                                                    selectedTerm={selectedTerm}
                                                    setSelectedTerm={setSelectedTerm}
                                                    selectedDegree={selectedDegree}
                                                    setSelectedDegree={setSelectedDegree}
                                                    selectedPrograms={selectedPrograms}
                                                    setSelectedPrograms={setSelectedPrograms}
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    programs={hubPrograms}
                                                    degrees={filteredDegrees}
                                                    intakeTerms={filteredTerms}
                                                    programsPerPage={programsPerPage}
                                                    loadingTerms={loadingTerms}
                                                    loadingDegrees={loadingDegrees}
                                                    loadingPrograms={loadingPrograms}
                                                    maxSelectable={maxSelectable}
                                                    onApplyNow={nextStep}
                                                />
                                            )}

                                            {steps[currentStep]?.id === "personal" && <PersonalInfo values={personalInfo} onChange={handlePersonalInfoChange} onValidSubmit={async () => {
                                                setPersonalInfoError(null);
                                                if (appNumber) {
                                                    setSavingPersonalInfo(true);
                                                    try {
                                                        const result = await savePersonalInfo(appNumber, {
                                                            first_name: personalInfo.firstName || undefined,
                                                            last_name: personalInfo.lastName || undefined,
                                                            preferred_name: personalInfo.preferredName || undefined,
                                                            citizenship: personalInfo.citizenship || undefined,
                                                            secondary_citizenship: personalInfo.secondaryCitizenship || undefined,
                                                            gender: personalInfo.gender || undefined,
                                                            national_identity_number: personalInfo.nationalIdentityNumber || undefined,
                                                            date_of_birth: personalInfo.dateOfBirth || undefined,
                                                            country_of_birth: personalInfo.countryOfBirth || undefined,
                                                            city_of_birth: personalInfo.cityOfBirth || undefined,
                                                            phone: personalInfo.mobileNumber?.trim() || undefined,
                                                            country_phone_code: personalInfo.mobileCode?.trim() || undefined,
                                                            father_name: personalInfo.fatherName || undefined,
                                                            mother_name: personalInfo.motherName || undefined,
                                                            address_country: personalInfo.addressCountry || undefined,
                                                            address_street: personalInfo.addressStreet || undefined,
                                                            address_city: personalInfo.addressCity || undefined,
                                                            address_zip: personalInfo.addressZip || undefined,
                                                            university_alumni: personalInfo.isAlumni || false,
                                                            application_salesforce_id: salesforceIds.application_salesforce_id,
                                                            // Identity document fields
                                                            identity_number: personalInfo.identityNumber || undefined,
                                                            identity_date_of_issue: personalInfo.identityDateOfIssue || undefined,
                                                            identity_date_of_expiry: personalInfo.identityDateOfExpiry || undefined,
                                                            identity_issuing_country: personalInfo.identityIssuingCountry || undefined,
                                                        });
                                                        if (result.status !== "success") {
                                                            setPersonalInfoError("Failed to save personal info. Please try again.");
                                                            setSavingPersonalInfo(false);
                                                            return;
                                                        }
                                                        setSalesforceIds((prev: typeof salesforceIds) => ({
                                                            ...prev,
                                                            salesforce_id: result.salesforce_id,
                                                            account_salesforce_id: result.account_salesforce_id,
                                                        }));
                                                    } catch {
                                                        setPersonalInfoError("Failed to save personal info. Please try again.");
                                                        setSavingPersonalInfo(false);
                                                        return;
                                                    } finally {
                                                        setSavingPersonalInfo(false);
                                                    }
                                                }
                                                nextStep();
                                            }} fieldConfig={personalInfoFieldConfig} countries={countries} hiddenFields={branding?.branding?.hidden_fields} genderIdentities={branding?.branding?.gender_identities} institutionCountry={branding?.institution_country} error={personalInfoError} />}

                                            {steps[currentStep]?.id === "educational" && <EducationalHistory
                                                appNumber={appNumber}
                                                onRequirementChange={setEduRequirementMet}
                                                cvVisibility={{
                                                    showProficiencyExam: selectedProgramData?.requires_proficiency_exam ?? true,
                                                    showJobExperience: selectedProgramData?.requires_job_experience ?? true,
                                                    showReference: selectedProgramData?.requires_references ?? true,
                                                }}
                                                degrees={branding?.branding?.degrees}
                                                gradingSystems={branding?.branding?.grading_systems}
                                                proficiencyExams={branding?.branding?.proficiency_exams}
                                                jobTypes={branding?.branding?.job_types}
                                                hideGpa={branding?.branding?.hide_gpa}
                                            />}

                                            {steps[currentStep]?.id === "questionnaire" && <Questionnaire ref={questionnaireRef} appNumber={appNumber} onRequirementChange={setQuestionnaireRequirementMet} />}

                                            {steps[currentStep]?.id === "documents" && <Documents appNumber={appNumber} onRequirementChange={setDocsRequirementMet} />}

                                            {steps[currentStep]?.id === "completed" && <CompleteScreen appNumber={appNumber} summary={documentsSummary} returnLabel="Return to Applications" onReturn={() => {
                                                    const newApp: Application = {
                                                        Application_Number: applicationNumber ?? `APP-${Date.now().toString().slice(-6)}`,
                                                        Application_External_ID: "",
                                                        Applicant_Name: documentsSummary.fullName,
                                                        Applied_Program_Name: documentsSummary.appliedProgram,
                                                        Stage: "Evaluation",
                                                        Applied_Term: documentsSummary.appliedTerm,
                                                        Application_Status: "",
                                                        Action: "View",
                                                        Updated_At: "Just now",
                                                        Date: new Date(),
                                                        summary: documentsSummary,
                                                    };
                                                    onSubmit(newApp);
                                                }} />}
                                        </motion.div>
                                    </AnimatePresence>

                                    {/* Footer */}
                                    <div className="relative flex items-center justify-between mt-8 md:mt-16 pt-6 md:pt-10 border-t border-slate-100">
                                        {!isCompletedStep && (
                                            <>
                                                {programSelectionError && currentStepId === "selection" && (
                                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-24 md:bottom-28 z-50 flex items-center gap-2.5 bg-red-50 border border-red-300 text-red-800 text-xs font-black uppercase tracking-wide px-4 py-2.5 rounded-2xl shadow-lg">
                                                        <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                                                        {programSelectionError}
                                                    </div>
                                                )}
                                                {showEduWarning && currentStepId === "educational" && (
                                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-24 md:bottom-28 z-50 flex items-center gap-2.5 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-black uppercase tracking-wide px-4 py-2.5 rounded-2xl shadow-lg">
                                                        <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                                                        Please add the required education record before continuing
                                                    </div>
                                                )}
                                                {showQuestionnaireWarning && currentStepId === "questionnaire" && (
                                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-24 md:bottom-28 z-50 flex items-center gap-2.5 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-black uppercase tracking-wide px-4 py-2.5 rounded-2xl shadow-lg">
                                                        <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                                                        Please answer all required questions before continuing
                                                    </div>
                                                )}
                                                {showDocsWarning && currentStepId === "documents" && (
                                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-24 md:bottom-28 z-50 flex items-center gap-2.5 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-black uppercase tracking-wide px-4 py-2.5 rounded-2xl shadow-lg">
                                                        <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                                                        Please upload all required documents before submitting
                                                    </div>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    onClick={() =>
                                                        isFirstStep
                                                            ? onCancel()
                                                            : prevStep()
                                                    }
                                                    className="text-xs font-black text-slate-900 hover:text-[#001B44] tracking-[0.2em] uppercase px-4 md:px-8"
                                                >
                                                    {isFirstStep ? "Cancel" : "Back"}
                                                </Button>
                                                <Button
                                                    onClick={async () => {
                                                        if (currentStepId === "application-type") {
                                                            nextStep();
                                                        } else if (currentStepId === "selection") {
                                                            if (selectedPrograms.length === 0) return;
                                                            setProgramSelectionError(null);

                                                            // Show reorder modal when multiple programs selected
                                                            if (selectedPrograms.length > 1 && !showReorderModal) {
                                                                setShowReorderModal(true);
                                                                return;
                                                            }

                                                            await handleCreateApplication();
                                                        } else if (currentStepId === "personal") {
                                                            const form = document.getElementById("personal-info-form") as HTMLFormElement | null;
                                                            if (form) {
                                                                form.requestSubmit();
                                                            }
                                                        } else if (currentStepId === "educational" && !eduRequirementMet) {
                                                            setShowEduWarning(true);
                                                            setTimeout(() => setShowEduWarning(false), 4000);
                                                        } else if (currentStepId === "questionnaire") {
                                                            if (!questionnaireRequirementMet) {
                                                                setShowQuestionnaireWarning(true);
                                                                setTimeout(() => setShowQuestionnaireWarning(false), 4000);
                                                                return;
                                                            }
                                                            setSavingQuestionnaire(true);
                                                            try {
                                                                await questionnaireRef.current?.save();
                                                            } finally {
                                                                setSavingQuestionnaire(false);
                                                            }
                                                            nextStep();
                                                        } else if (currentStepId === "documents") {
                                                            if (!docsRequirementMet) {
                                                                setShowDocsWarning(true);
                                                                setTimeout(() => setShowDocsWarning(false), 4000);
                                                                return;
                                                            }
                                                            setSubmittingApplication(true);
                                                            try {
                                                                await submitApplication({ application_number: appNumber });
                                                            } catch {
                                                                // proceed regardless — show complete screen even on failure
                                                            } finally {
                                                                setSubmittingApplication(false);
                                                            }
                                                            onSubmit(buildFinalApp());
                                                            nextStep();
                                                        } else {
                                                            nextStep();
                                                        }
                                                    }}
                                                    disabled={
                                                        creatingApplication ||
                                                        submittingApplication ||
                                                        savingQuestionnaire ||
                                                        (currentStepId === "application-type" && !selectedApplicationType) ||
                                                        (currentStepId === "selection" &&
                                                        !appNumber &&
                                                        (!selectedTerm ||
                                                            !selectedDegree ||
                                                            selectedPrograms.length === 0))
                                                    }
                                                    className="rounded-xl md:rounded-2xl px-6 md:px-12 h-11 md:h-14 font-black bg-[#001B44] hover:bg-[#002b66] text-white transition-all active:scale-95 text-xs md:text-sm uppercase tracking-[0.1em] flex items-center gap-2"
                                                >
                                                    {currentStepId === "documents"
                                                        ? submittingApplication ? "Submitting…" : "Submit Application"
                                                        : creatingApplication
                                                          ? <><Loader2 className="w-4 h-4 animate-spin" />Creating…</>
                                                          : savingPersonalInfo
                                                            ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</>
                                                            : savingQuestionnaire
                                                              ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</>
                                                              : "Save & Continue"}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
        </div>

        {/* ── Reorder Modal ───────────────────────────────────────── */}
        {showReorderModal && (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
                role="presentation"
                onClick={(clickEvent) => {
                    if (clickEvent.target === clickEvent.currentTarget) setShowReorderModal(false);
                }}
            >
                <div
                    ref={reorderModalRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="reorder-modal-title"
                    className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 flex flex-col gap-6"
                >
                    <div>
                        <h3 id="reorder-modal-title" className="text-xl font-black text-[#001B44]">Arrange Your Preferences</h3>
                        <p className="text-sm text-slate-500 mt-1 font-medium">
                            Drag or use the arrows to set your order of preference. The first program is your primary choice.
                        </p>
                    </div>
                    <ol className="flex flex-col gap-3">
                        {selectedPrograms.map((progId, index) => {
                            const prog = hubPrograms.find((p) => p.id === progId);
                            if (!prog) return null;
                            return (
                                <li
                                    key={progId}
                                    draggable
                                    onDragStart={(dragEvent) => {
                                        dragEvent.dataTransfer.effectAllowed = "move";
                                        dragEvent.dataTransfer.setData("text/plain", String(index));
                                    }}
                                    onDragOver={(dragEvent) => {
                                        dragEvent.preventDefault();
                                        dragEvent.dataTransfer.dropEffect = "move";
                                    }}
                                    onDrop={(dragEvent) => {
                                        dragEvent.preventDefault();
                                        const fromIndex = parseInt(dragEvent.dataTransfer.getData("text/plain"), 10);
                                        if (fromIndex === index) return;
                                        const next = [...selectedPrograms];
                                        const [moved] = next.splice(fromIndex, 1);
                                        next.splice(index, 0, moved);
                                        setSelectedPrograms(next);
                                    }}
                                    className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 cursor-grab active:cursor-grabbing active:opacity-60 active:border-[#001B44] transition-opacity"
                                >
                                    <span className="w-7 h-7 rounded-full bg-[#001B44] text-white flex items-center justify-center text-xs font-black shrink-0">
                                        {index + 1}
                                    </span>
                                    <span className="flex-1 text-sm font-bold text-slate-800 truncate">{prog.name}</span>
                                    <div className="flex flex-col gap-0.5">
                                        <button
                                            disabled={index === 0}
                                            onClick={() => {
                                                const next = [...selectedPrograms];
                                                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                                setSelectedPrograms(next);
                                            }}
                                            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-slate-200 disabled:opacity-20 disabled:cursor-not-allowed text-slate-500 transition-all"
                                        >
                                            <ChevronLeft className="w-3 h-3 rotate-90" />
                                        </button>
                                        <button
                                            disabled={index === selectedPrograms.length - 1}
                                            onClick={() => {
                                                const next = [...selectedPrograms];
                                                [next[index + 1], next[index]] = [next[index], next[index + 1]];
                                                setSelectedPrograms(next);
                                            }}
                                            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-slate-200 disabled:opacity-20 disabled:cursor-not-allowed text-slate-500 transition-all"
                                        >
                                            <ChevronLeft className="w-3 h-3 -rotate-90" />
                                        </button>
                                    </div>
                                    <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
                                </li>
                            );
                        })}
                    </ol>
                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => setShowReorderModal(false)}
                            className="flex-1 h-12 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-500"
                        >
                            Back
                        </Button>
                        <Button
                            onClick={handleCreateApplication}
                            disabled={creatingApplication}
                            className="flex-1 h-12 rounded-2xl font-black bg-[#001B44] hover:bg-[#002b66] text-white text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                        >
                            {creatingApplication ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</> : "Confirm & Continue"}
                        </Button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
